import { useEffect, useRef, useState } from 'react';
import Field from '../../../components/common/Field';
import Spinner from '../../../components/common/Spinner';
import CropPhotoModal from '../../../components/common/CropPhotoModal';
import useSignupStore from '../../../store/useSignupStore';

const ROLES = [
  { value: 'Mom', emoji: '👩' },
  { value: 'Dad', emoji: '👨' },
];

/* Below this the zip is still being typed — no point asking Places about it. */
const ZIP_LOOKUP_MIN = 5;

export default function ProfileStep() {
  const { firstName, lastName, zipCode, zipStatus, role, photo, photoPreview, location } = useSignupStore();
  const set = useSignupStore((s) => s.set);
  const goNext = useSignupStore((s) => s.goNext);
  const lookupZip = useSignupStore((s) => s.lookupZip);
  const fileRef = useRef(null);
  const [cropFile, setCropFile] = useState(null); // picked file awaiting the cropper

  /* The zip resolves itself as it's typed: 5 or 6 digits triggers a debounced
     Places lookup, and the address it finds is what the schools step measures
     distance from. Stepping back here doesn't re-ask — the answer is in the
     store already. */
  useEffect(() => {
    if (zipCode.length < ZIP_LOOKUP_MIN || zipStatus !== 'idle') return undefined;
    const t = setTimeout(() => lookupZip(zipCode), 400);
    return () => clearTimeout(t);
  }, [zipCode, zipStatus, lookupZip]);

  /* A picked file goes through the cropper first; only the cropped square is
     kept. The preview URL outlives this step — stepping forward and back must
     not break the image — so it's only released when it's replaced, or when the
     wizard resets. */
  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setCropFile(file);
  };
  const usePhoto = (cropped) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    set({ photo: cropped, photoPreview: URL.createObjectURL(cropped) });
    setCropFile(null);
  };

  const changeZip = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, 6);
    if (digits === zipCode) return;
    /* a new zip invalidates the point we resolved from the old one, and with it
       the school list those grade picks came from */
    set({
      zipCode: digits,
      zipStatus: 'idle',
      location: null,
      joinGroups: [],
      roomParentGrade: '',
      joinClassId: '',
    });
  };

  /* The photo is required (Mohit, 29 Sep) — the red "*Add Photo Profile" label
     was already marking it so; now Continue holds until one is cropped in. */
  const detailsDone = firstName.trim() && role && location;
  const complete = detailsDone && photo;

  return (
    <>
      <div className="ob-eyebrow">Welcome</div>
      <h1>Let's set up <em>your profile!</em></h1>
      <p className="sub">Here's what you need to start. You can add more later.</p>

      <div className="ob-photo-block">
        <button type="button" className="ob-photo" onClick={() => fileRef.current?.click()}>
          {photoPreview ? (
            <img src={photoPreview} alt="" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="12" cy="8" r="3.6" />
              <path d="M4.5 19.5c1.2-3.6 4-5.4 7.5-5.4s6.3 1.8 7.5 5.4" strokeLinecap="round" />
            </svg>
          )}
          <span className="ob-photo-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 8.5h3.2L8 6h8l1.8 2.5H21v10H3z" strokeLinejoin="round" />
              <circle cx="12" cy="13" r="3.2" />
            </svg>
          </span>
        </button>
        <button type="button" className="ob-photo-label" onClick={() => fileRef.current?.click()}>
          {photoPreview ? 'Change Photo Profile' : '*Add Photo Profile'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} />
      </div>

      <div className="form-stack">
        <div className="form-grid">
          <Field label="First Name">
            <input
              type="text"
              value={firstName}
              placeholder="Jamie"
              onChange={(e) => set({ firstName: e.target.value })}
            />
          </Field>
          <Field label="Last Name">
            <input
              type="text"
              value={lastName}
              placeholder="Rivera"
              onChange={(e) => set({ lastName: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Zip Code" className={zipStatus === 'notfound' ? 'missing' : ''}>
          <input
            type="text"
            value={zipCode}
            placeholder="73301"
            inputMode="numeric"
            maxLength={6}
            onChange={(e) => changeZip(e.target.value)}
          />
          {zipStatus === 'checking' && (
            <div className="ob-zip-status"><Spinner variant="green" /> Looking up ZIP code…</div>
          )}
          {zipStatus === 'found' && location && (
            <div className="ob-zip-status found">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" strokeLinejoin="round" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
              <span>{location.address || location.name}</span>
            </div>
          )}
          {zipStatus === 'notfound' && (
            <div className="ob-zip-status bad">We couldn't find that ZIP code — check it and try again.</div>
          )}
        </Field>

        <Field label="I'm a..">
          <div className="ob-choice-row">
            {ROLES.map((r) => (
              <button
                key={r.value}
                type="button"
                className={`ob-choice ${role === r.value ? 'on' : ''}`}
                onClick={() => set({ role: r.value })}
              >
                <span className="em">{r.emoji}</span>
                {r.value}
              </button>
            ))}
          </div>
        </Field>

        <button className="btn btn-primary btn-primary-full" disabled={!complete} onClick={goNext}>
          Continue
        </button>
        {/* everything else is in — say why Continue is still greyed out */}
        {detailsDone && !photo && (
          <div className="hint" style={{ textAlign: 'center', marginTop: -4 }}>
            Add a profile photo to continue.
          </div>
        )}
      </div>

      <CropPhotoModal file={cropFile} onCancel={() => setCropFile(null)} onDone={usePhoto} />
    </>
  );
}
